var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useContext, useMemo } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTRPC } from "@iscr/xAppLib/context/QueryProvider";
import { useUser } from "@iscr/xAppLib/Hooks";
import { defer } from "@iscr/xAppLib/helpers/defer";
import { EulaModal } from "@iscr/mims/components/EulaModal";
import { useMimsFeatureState } from "@iscr/mims/hooks/useMimsFeatureState";
const ONE_HOUR_IN_MS = 1000 * 60 * 60;
const Context = React.createContext({
    value: null,
    actions: {
        viewEula: () => Promise.reject(),
        acceptEula: () => Promise.reject(),
    },
    can: () => false,
});
export function MimsRegistrationProvider({ children }) {
    // This is just here to ensure this component re-renders when the `app` object changes.
    useUser();
    const trpc = useTRPC();
    const flag = useMimsFeatureState();
    const enabled = Boolean(app.acl.is_work) && flag !== false;
    const getStatus = useQuery(trpc.mims.user.status.queryOptions(undefined, {
        refetchInterval: ONE_HOUR_IN_MS,
        enabled,
    }));
    const accept = useMutation(trpc.mims.eula.accept.mutationOptions());
    const status = (enabled && getStatus.data) || null;
    const [viewEula, setViewEula] = React.useState(null);
    const actions = useMemo(() => ({
        viewEula: () => {
            const deferred = defer();
            setViewEula(deferred);
            deferred.promise.finally(() => setViewEula(null));
            return deferred.promise;
        },
        acceptEula: (revisionDate) => __awaiter(this, void 0, void 0, function* () {
            yield accept.mutateAsync({ revisionDate });
            viewEula === null || viewEula === void 0 ? void 0 : viewEula.resolve(true);
            yield getStatus.refetch();
        }),
    }), [status]);
    const value = useMemo(() => ({
        value: status,
        actions,
        can: (action) => (status === null || status === void 0 ? void 0 : status.actions.includes(action)) || false,
    }), [status, actions]);
    return (_jsxs(Context.Provider, { value: value, children: [children, (status === null || status === void 0 ? void 0 : status.type) && (_jsx(EulaModal, { type: status.type, onChange: visible => !visible && (viewEula === null || viewEula === void 0 ? void 0 : viewEula.resolve(false)), onAccept: actions.acceptEula, value: !!viewEula }))] }));
}
export function useMimsRegistrationState() {
    const value = useContext(Context);
    if (value === null) {
        throw new Error("useMimsRegistrationState must be used within a MimsRegistrationProvider");
    }
    return value;
}
export function useMimsDataVersion() {
    var _a;
    const mims = useMimsRegistrationState();
    return ((_a = mims.value) === null || _a === void 0 ? void 0 : _a.dataVersion) || null;
}
